<template>
  <div>
    <div class="tw-flex tw-justify-end">
      <base-button type="flat" class="tw-mr-2" size="sm" @click="$refs.filterSlideOver.show(eventTypesFilter)">
        <template #icon>
          <filter-icon />
        </template>
        Filters
        <base-badge class="tw-ml-2" color="primary" size="sm" v-if="filterCount">{{ filterCount }}</base-badge>
      </base-button>
      <base-button @click="$refs.commentModal.show({})" size="sm">
        <template #icon>
          <plus-icon />
        </template>
        Add Comment
      </base-button>
    </div>
    <base-table :loading="isLoading" :fields="fields" :items="items" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" class="tw-mt-4">
      <template #icon="{item: event}">
        <div
          class="tw-p-1.5 tw-rounded-full"
          :class="{ 'tw-bg-primary-50 tw-text-primary-500': event.type !== 'Disputed Charge', 'tw-bg-red-50 tw-text-red-500': event.type === 'Disputed Charge' }"
        >
          <component :is="getComponentIcon(event.type)" class="tw-h-4 tw-w-4" />
        </div>
      </template>
      <template #event="{ item: event}">
        <component
          :is="getComponentType(event.type)"
          :business-id="businessId"
          :user-id="userId"
          :event="event"
          @editComment="$refs.commentModal.show($event)"
          @refundCharge="$refs.refundModal.show($event)"
        />
      </template>
      <template #date="{ item: event}">
        <div class="tw-truncate">
          {{ formatEventDate(event.eventDate) }}
        </div>
        <div class="tw-text-gray-500 tw-text-xs tw--mt-0.5">{{ event.userFullName }}</div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-pl-4">
          <base-skeleton-loader class="tw-h-7 tw-w-7 tw-rounded-full" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24 tw-mt-1.5" />
          <base-skeleton-loader class="tw-h-2 tw-w-60 tw-mt-1.5 tw-mb-1.5" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-2 tw-w-12" />
        </td>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <comment-modal ref="commentModal" :business-id="businessId" :user-id="userId" :full-name="member.fullName" @refresh="loadPage" />
    <refund-modal ref="refundModal" :business-id="businessId" :user-id="userId" @refresh="loadPage" />
    <filter-slide-over ref="filterSlideOver" @filter-updated="updateFilter" />
  </div>
</template>

<script>
import CommentModal from "@/views/business/member/history/components/CommentModal";
import RefundModal from "@/views/business/member/history/components/RefundModal";
import FilterSlideOver from "@/views/business/member/history/components/FilterSlideOver";
import BusinessMemberHistoryBaseEvent from "@/views/business/member/history/components/events/BusinessMemberHistoryBaseEvent";
import BusinessMemberHistoryCreditEvent from "@/views/business/member/history/components/events/BusinessMemberHistoryCreditEvent";
import BusinessMemberHistoryChargeEvent from "@/views/business/member/history/components/events/BusinessMemberHistoryChargeEvent";
import BusinessMemberHistoryCommentEvent from "@/views/business/member/history/components/events/BusinessMemberHistoryCommentEvent";
import BusinessMemberHistoryRefundEvent from "@/views/business/member/history/components/events/BusinessMemberHistoryRefundEvent";
import BusinessMemberHistoryDisputeEvent from "@/views/business/member/history/components/events/BusinessMemberHistoryDisputeEvent";
import { PlusIcon, LocationIcon, FilterIcon, CreditCardIcon, ShoppingBagIcon, PillIcon, RxIcon, MoneyIcon, TaskListIcon, CommentIcon, StarIcon, AlertIcon } from "@/assets/svgs";
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  components: {
    CommentModal,
    RefundModal,
    FilterSlideOver,
    BusinessMemberHistoryBaseEvent,
    BusinessMemberHistoryCreditEvent,
    BusinessMemberHistoryChargeEvent,
    BusinessMemberHistoryCommentEvent,
    BusinessMemberHistoryRefundEvent,
    BusinessMemberHistoryDisputeEvent,
    PlusIcon,
    LocationIcon,
    FilterIcon,
    CreditCardIcon,
    ShoppingBagIcon,
    PillIcon,
    RxIcon,
    MoneyIcon,
    TaskListIcon,
    CommentIcon,
    StarIcon,
    AlertIcon,
  },
  props: {
    businessId: String,
    userId: String,
    member: Object,
  },
  data() {
    return {
      isLoading: false,
      page: {
        take: 10,
        skip: 0,
        total: 0,
      },
      sortKey: "date",
      sortDesc: true,
      eventTypesFilter: [],
      fields: [{ title: "", key: "icon", thClass: "tw-w-0", tdClass: "tw-pr-0" }, { title: "Event", key: "event" }, { title: "Date", key: "date", isSortable: true }],
      items: [],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    locale() {
      return window.navigator.language;
    },
    filterCount() {
      return this.eventTypesFilter.length;
    },
  },
  created() {
    this.EventBus.$on("member_update", this.loadPage);

    // Check if the 'comment' query parameter is true
    if (this.$route.query.comment === "true") {
      // Add 'Comment' to eventTypesFilter if it's not already there
      if (!this.eventTypesFilter.includes("Comment")) {
        this.eventTypesFilter.push("comment");
      }
      // remove the query parameter from the URL after processing
      const query = { ...this.$route.query };
      delete query.comment;
      this.$router.replace({ query });
    }

    this.loadPage();
  },
  methods: {
    async loadPage() {
      try {
        this.isLoading = true;
        const response = await this.$axios.post(
          `/businesses/${this.businessId}/members/${this.userId}/history?skip=${this.page.skip}&take=${this.page.take}&desc=${this.sortDesc}`,
          {
            eventTypes: this.eventTypesFilter,
            usePrescriptionProgramSetting: this.organization.usePrescriptionProgramSetting,
            useCreditsSetting: this.organization.useCreditsSetting,
            useTodoSetting: this.organization.useTodoSetting,
            useTipsSetting: this.organization.useTipsSetting,
          },
        );
        this.items = response.data.items;
        this.page.take = response.data.take;
        this.page.skip = response.data.skip;
        this.page.total = response.data.total;
      } finally {
        this.isLoading = false;
      }
    },
    formatEventDate(date) {
      return moment
        .utc(date)
        .local()
        .format("M/D/YY h:mm A");
    },
    getComponentType(eventType) {
      if (eventType.toLowerCase().includes("dispute")) return "business-member-history-dispute-event";
      else if (eventType.toLowerCase().includes("credit")) return "business-member-history-credit-event";
      else if (eventType.toLowerCase().includes("charge")) return "business-member-history-charge-event";
      else if (eventType.toLowerCase().includes("tip")) return "business-member-history-charge-event";
      else if (eventType.toLowerCase().includes("comment")) return "business-member-history-comment-event";
      else if (eventType.toLowerCase().includes("refund")) return "business-member-history-refund-event";
      return "business-member-history-base-event";
    },
    getComponentIcon(eventType) {
      if (eventType.toLowerCase().includes("plan change")) return "shopping-bag-icon";
      else if (eventType.toLowerCase().includes("card changed")) return "credit-card-icon";
      else if (eventType.toLowerCase().includes("medication change")) return "pill-icon";
      else if (eventType.toLowerCase().includes("dispute")) return "alert-icon";
      else if (eventType.toLowerCase().includes("refund") || eventType.toLowerCase().includes("charge") || eventType.toLowerCase().includes("tip")) return "money-icon";
      else if (eventType.toLowerCase().includes("credit")) return "star-icon";
      else if (eventType.toLowerCase().includes("comment")) return "comment-icon";
      else if (
        eventType.toLowerCase().includes("rx status change") ||
        eventType.toLowerCase().includes("rx created") ||
        eventType.toLowerCase().includes("rx onboarding invite sent")
      )
        return "rx-icon";

      return "task-list-icon";
    },
    updateFilter({ eventTypesFilter }) {
      this.eventTypesFilter = eventTypesFilter;
      this.page.skip = 0;
    },
  },
  watch: {
    page() {
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
    userId() {
      this.loadPage();
    },
    eventTypesFilter() {
      this.loadPage();
    },
  },
};
</script>
